/**
 * Client for the admin dashboard API. All endpoints are admin-gated on the
 * backend (ADMIN_EMAILS allowlist); the email check here only controls UI
 * visibility and is NOT a security boundary.
 */
import { auth } from './firebase';
import { Attribution } from './attribution';

// REACT_APP_ADMIN_BACKEND_URL points the dashboard at a local backend
// (e.g. http://localhost:7860) for development.
const BACKEND_URL =
  process.env.REACT_APP_ADMIN_BACKEND_URL ||
  'https://call-for-me-prod-3055119120.us-central1.run.app';

/** Emails allowed to see the admin UI. Backend enforces the real check. */
const ADMIN_EMAILS = ['makatchev@gmail.com'];

export function isAdminEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  return ADMIN_EMAILS.includes(email.toLowerCase());
}

/** A failed admin API call, with its HTTP status (0 when the request never
 * got a response). */
export class AdminApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'AdminApiError';
    this.status = status;
  }
}

async function adminFetch<T>(
  path: string,
  init?: { method: 'POST' | 'PUT' | 'DELETE'; body?: unknown }
): Promise<T> {
  const token = await auth.currentUser?.getIdToken();
  let res: Response;
  try {
    res = await fetch(`${BACKEND_URL}${path}`, {
      method: init?.method || 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        ...(init?.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      body: init?.body !== undefined ? JSON.stringify(init.body) : undefined,
    });
  } catch (e) {
    throw new AdminApiError(e instanceof Error ? e.message : 'Network error', 0);
  }
  if (!res.ok) {
    if (res.status === 403) throw new AdminApiError('You do not have admin access.', 403);
    if (res.status === 401) throw new AdminApiError('Please sign in again.', 401);
    const body = await res.json().catch(() => ({}));
    throw new AdminApiError(formatDetail(body.detail) || `Request failed (${res.status})`, res.status);
  }
  return res.json() as Promise<T>;
}

/** FastAPI errors are a string, or a list of validation errors (422). */
function formatDetail(detail: unknown): string {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => `${(d.loc || []).filter((l: unknown) => l !== 'body').join('.')}: ${d.msg}`)
      .join('; ');
  }
  return '';
}

export interface AnalyticsSummary {
  totals: {
    users: number;
    calls: number;
    payments: number;
    creditsOutstanding: number;
    creditsGranted: number;
    totalCallDurationSeconds: number;
  };
  usersByPlan: Record<string, number>;
  callsByStatus: Record<string, number>;
  callsBySource: Record<string, number>;
  /** One per paymentFulfillments document: checkouts plus renewals. */
  paymentsByPlan: Record<string, number>;
  /** Missing from backends deployed before subscription tracking. */
  subscriptions?: SubscriptionMetrics;
  signupsByDay: Record<string, number>;
  callsByDay: Record<string, number>;
  attribution: {
    utmSources: Record<string, number>;
    utmCampaigns: Record<string, number>;
  };
}

/** Subscription metrics, without ADMIN_EMAILS accounts. */
export interface SubscriptionMetrics {
  activeSubscribers: number;
  /** Keyed "plan / interval", e.g. "callout_10 / month". */
  activeByPlan: Record<string, number>;
  cancellationsScheduled: number;
  churned30d: number;
  churned90d: number;
  failedPayments30d: number;
  failedPayments90d: number;
  failedPaymentUsers30d: number;
  planChanges90d: number;
  /** Distinct users with a fulfilled payment. */
  payingUsers: number;
  payingUsersByPlan: Record<string, number>;
  /** Stripe events that matched no profile (so no profile changed). */
  unmatchedEvents: number;
  /** Subscribers with no checkout or renewal for over a billing period. */
  possiblyLapsed: {
    uid: string;
    email: string | null;
    plan: string | null;
    billingInterval: string | null;
    lastPaymentAt: string | null;
    hasStripeCustomer: boolean;
  }[];
  recentEvents: (SubscriptionEvent & { userId: string | null; email?: string | null; at: string | null })[];
  excludedAdminAccounts: number;
  adminSubscribers: number;
}

/** A subscriptionEvents document, recorded from Stripe webhooks. */
export interface SubscriptionEvent {
  type:
    | 'cancellation_scheduled'
    | 'cancellation_undone'
    | 'cancellation_feedback'
    | 'plan_changed'
    | 'subscription_ended'
    | 'payment_failed'
    | string;
  planName?: string | null;
  billingInterval?: string | null;
  previousPlanName?: string | null;
  previousBillingInterval?: string | null;
  planChange?: 'upgrade' | 'downgrade' | 'interval_change' | null;
  status?: string | null;
  cancelAt?: string | null;
  cancelledAt?: string | null;
  endedAt?: string | null;
  cancellationReason?: string | null;
  cancellationFeedback?: string | null;
  cancellationComment?: string | null;
  /** subscription_ended while another subscription was still active. */
  replaced?: boolean | null;
  amountDue?: number | null;
  currency?: string | null;
  attemptCount?: number | null;
  invoiceId?: string | null;
  stripeSubscriptionId?: string | null;
  eventCreatedAt?: string | null;
}

export interface AdminUserRow {
  uid: string;
  email: string | null;
  displayName: string | null;
  subscriptionPlan: string | null;
  creditsRemaining: number | null;
  createdAt: string | null;
  callCount: number;
  lastCallAt: string | null;
  creditsPurchased: number;
  attribution?: Attribution;
}

export interface CallSummary {
  callId: string;
  callSid: string;
  toNumber: string;
  fromNumber: string;
  status: string | null;
  // 'machine' when the bot heard an answering-machine beep, 'fax' for a fax.
  answeredBy?: string | null;
  duration: number | null;
  source: string | null;
  mode: string | null;
  workflow: string | null;
  creditDeducted: boolean | null;
  hasPrompt: boolean;
  hasAudio: boolean;
  hasTranscript: boolean;
  initiatedAt: string | null;
  completedAt: string | null;
  /** Set once the user deleted the call; see DeletionStatus. */
  deletionStatus?: DeletionStatus | null;
}

/**
 * A call the user deleted: 'pending' while its content is kept for review
 * (24 hours), 'purged' once its recording, transcript and prompt are erased.
 * The call's accounting facts stay either way (call_deletion.py).
 */
export type DeletionStatus = 'pending' | 'purged';

export interface PaymentRecord {
  sessionId?: string;
  invoiceId?: string;
  eventType?: string;
  planName?: string;
  billingInterval?: string;
  creditsGranted?: number;
  status?: string;
  createdAt?: string | null;
}

export interface JourneyEvent {
  userId?: string;
  callId?: string;
  eventName?: string;
  eventStage?: string;
  timestamp?: string;
  source?: string;
  payload?: Record<string, any>;
  /** For email_received: the emailLog id whose body `emailAdminApi.emailLog` loads. */
  emailLogId?: string;
  /** Self-reported (the public NPS form): anyone could have submitted it. */
  unverified?: boolean;
}

export interface JourneyRecord {
  uid: string;
  email: string | null;
  displayName: string | null;
  subscriptionPlan: string | null;
  creditsRemaining: number | null;
  createdAt: string | null;
  eventCount: number;
  timeline: JourneyEvent[];
}

export interface UserDetail {
  uid: string;
  profile: Record<string, any> | null;
  authInfo: {
    email: string | null;
    displayName: string | null;
    emailVerified: boolean;
    disabled: boolean;
    providers: string[];
    createdAt: string | null;
    lastSignInAt: string | null;
  } | null;
  calls: CallSummary[];
  payments: PaymentRecord[];
  subscriptionEvents?: SubscriptionEvent[];
  emails?: EmailLogEntry[];
  feedback?: UserFeedbackEntry[];
}

export interface EmailLogEntry {
  campaign: string | null;
  subject: string | null;
  status: string | null;
  lang: string | null;
  error: string | null;
  /** Plain-text body as sent; missing for emails sent before it was logged. */
  text?: string | null;
  createdAt: string | null;
  sentAt: string | null;
}

export interface UserFeedbackEntry {
  campaign: string | null;
  survey: string | null;
  metNeeds: string | null;
  useCases: string[] | null;
  issues: string[] | null;
  comment: string | null;
  updatedAt: string | null;
}

export interface CallDetail {
  callId: string;
  callSid: string;
  userId: string;
  userEmail: string | null;
  userName: string | null;
  /** The site the user signed up on ('jp' callout.jp, 'us' heycallout.com); absent on older profiles. */
  userMarket: string | null;
  toNumber: string;
  /** 'JP', 'US' or 'CA'; absent on calls made before it was recorded. */
  toCountry: string | null;
  fromNumber: string;
  /** 'ja' or 'en'; absent on calls made before it was recorded. */
  language: string | null;
  status: string | null;
  answeredBy?: string | null;
  duration: number | null;
  source: string | null;
  mode: string | null;
  workflow: string | null;
  objective: string | null;
  context: Record<string, any> | null;
  prompt: string | null;
  attribution: Attribution | null;
  landingPage: string | null;
  creditDeducted: boolean | null;
  creditsDeducted: number | null;
  reason: string | null;
  initiatedAt: string | null;
  completedAt: string | null;
  processedAt: string | null;
  audioUrl: string | null;
  transcriptUrl: string | null;
  deletionStatus?: DeletionStatus | null;
  deletionRequestedAt?: string | null;
  purgeAfter?: string | null;
  contentPurgedAt?: string | null;
  /** 'auto' after the review window, else the admin's email. */
  contentPurgedBy?: string | null;
}

export interface DeletedCallRow {
  callId: string;
  userId: string | null;
  userEmail: string | null;
  toNumber: string | null;
  status: string | null;
  duration: number | null;
  creditDeducted: boolean;
  initiatedAt: string | null;
  deletionStatus: DeletionStatus;
  deletionRequestedAt: string | null;
  purgeAfter: string | null;
  contentPurgedAt: string | null;
  contentPurgedBy: string | null;
  hasAudio: boolean;
  hasTranscript: boolean;
}

export interface DeletedCallsListing {
  pending: DeletedCallRow[];
  purged: DeletedCallRow[];
  reviewWindowHours: number;
}

export interface TranscriptMessage {
  // 'event' marks a non-speech sound the bot detected: a beep or a fax tone.
  role: 'user' | 'assistant' | 'event';
  content: string;
  timestamp?: string;
}

// ---------------------------------------------------------------------------
// Lifecycle email (see EMAIL.md)
// ---------------------------------------------------------------------------

export type EmailLang = 'en' | 'ja';
export const EMAIL_LANGS: EmailLang[] = ['en', 'ja'];

export interface TemplateCopy {
  subject: string;
  preheader: string;
  html: string;
  text: string;
  cta: string;
}

export interface EmailTemplateSummary {
  id: string;
  description: string;
  survey: string | null;
  subject: Record<EmailLang, string>;
  customized: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface EmailTemplateList {
  templates: EmailTemplateSummary[];
  variables: string[];
  surveys: string[];
  segments: string[];
}

export interface EmailTemplateDetail {
  id: string;
  description: string;
  survey: string | null;
  customized: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
  default: Record<EmailLang, TemplateCopy>;
  override: Partial<Record<EmailLang, Partial<TemplateCopy>>>;
  effective: Record<EmailLang, TemplateCopy>;
  variables: string[];
}

export interface RenderedEmail {
  subject: string;
  html: string;
  text: string;
}

export interface CustomEmailCopy {
  subject: string;
  html: string;
  text?: string;
  preheader?: string;
  cta?: string;
}

export type EmailSegmentType = 'recent_purchasers' | 'recent_signups';

export interface EmailSendRequest {
  campaign?: string;
  template?: string;
  content?: Partial<Record<EmailLang, CustomEmailCopy>>;
  survey?: string;
  variables?: Record<string, string>;
  recipients?: { email: string; name?: string }[];
  segment?: { type: EmailSegmentType; days: number };
}

export interface EmailDryRunResult {
  dryRun: true;
  campaign: string;
  configured: boolean;
  summary: Record<string, number>;
  recipients: { email: string; uid: string | null; lang: EmailLang; status: string }[];
  preview: RenderedEmail | null;
}

export type EmailSendJobStatus = 'running' | 'completed' | 'cancelled' | 'failed';

/** A real send (emailSendJobs), sent in short batches by `advanceSendJob`
 * calls and the backend's scheduler tick. */
export interface EmailSendJob {
  jobId: string;
  dryRun: false;
  campaign: string;
  template: string | null;
  audience: { type: string; days?: number } | null;
  status: EmailSendJobStatus;
  /** Running, but nothing has moved it forward for a few minutes. */
  stalled: boolean;
  total: number;
  processed: number;
  summary: Record<string, number>;
  error: string | null;
  createdBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  finishedAt: string | null;
  /** Per-recipient outcome so far; left out of the job list. */
  results?: { email: string; uid?: string | null; lang?: string; status: string; error?: string }[];
  /** The email as rendered for the first recipient (link tokens redacted).
   * Only `sendJob` returns it. */
  preview?: (RenderedEmail & { email: string; lang: EmailLang }) | null;
}

export interface FeedbackRow {
  id: string;
  campaign: string | null;
  survey: string | null;
  userId: string | null;
  email: string | null;
  lang: string | null;
  metNeeds: string | null;
  useCases: string[] | null;
  issues: string[] | null;
  comment: string | null;
  updatedAt: string | null;
}

export interface FeedbackListing {
  total: number;
  metNeeds: Record<string, number>;
  campaigns: Record<string, { total: number; metNeeds: Record<string, number> }>;
  responses: FeedbackRow[];
}

export const emailAdminApi = {
  templates: () => adminFetch<EmailTemplateList>('/admin/emails/templates'),
  template: (id: string) =>
    adminFetch<EmailTemplateDetail>(`/admin/emails/templates/${encodeURIComponent(id)}`),
  saveTemplate: (id: string, copy: Partial<Record<EmailLang, TemplateCopy>>) =>
    adminFetch<EmailTemplateDetail>(`/admin/emails/templates/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: copy,
    }),
  resetTemplate: (id: string) =>
    adminFetch<EmailTemplateDetail>(`/admin/emails/templates/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  previewTemplate: (
    id: string,
    body: { lang: EmailLang; draft?: TemplateCopy; recipient?: { email?: string; name?: string } }
  ) =>
    adminFetch<RenderedEmail>(`/admin/emails/templates/${encodeURIComponent(id)}/preview`, {
      method: 'POST',
      body,
    }),
  dryRun: (body: EmailSendRequest) =>
    adminFetch<EmailDryRunResult>('/admin/emails/send', {
      method: 'POST',
      body: { ...body, dryRun: true },
    }),
  /** Starts a send job and returns it at once; `advanceSendJob` sends it. */
  send: (body: EmailSendRequest) =>
    adminFetch<EmailSendJob>('/admin/emails/send', {
      method: 'POST',
      body: { ...body, dryRun: false },
    }),
  sendJob: (jobId: string) =>
    adminFetch<EmailSendJob>(`/admin/emails/send/${encodeURIComponent(jobId)}`),
  /** Sends the job's next batch (up to ~15 s of work) and returns its progress. */
  advanceSendJob: (jobId: string) =>
    adminFetch<EmailSendJob>(`/admin/emails/send/${encodeURIComponent(jobId)}/advance`, {
      method: 'POST',
    }),
  /** One sent email as logged, with its plain-text body (link tokens redacted). */
  emailLog: (logId: string) =>
    adminFetch<EmailLogEntry & { id: string; email: string | null; userId: string | null }>(
      `/admin/emails/log/${encodeURIComponent(logId)}`
    ),
  sendJobs: (limit = 10) =>
    adminFetch<{ jobs: EmailSendJob[] }>(`/admin/emails/send?limit=${limit}`),
  cancelSendJob: (jobId: string) =>
    adminFetch<EmailSendJob>(`/admin/emails/send/${encodeURIComponent(jobId)}/cancel`, {
      method: 'POST',
    }),
  /** Every email and survey answer, newest first (see `ActivityItem`). */
  activity: (limit = 300) =>
    adminFetch<{ items: ActivityItem[]; more: boolean }>(`/admin/emails/activity?limit=${limit}`),
  feedback: (campaign?: string) =>
    adminFetch<FeedbackListing>(
      `/admin/emails/feedback?limit=1000${campaign ? `&campaign=${encodeURIComponent(campaign)}` : ''}`
    ),
};

export interface NpsResponseRow {
  id: string;
  userId: string | null;
  email: string | null;
  /** False when the user ID isn't an existing user; left out of the score. */
  knownUser: boolean;
  npsScore: number | null;
  liked: string | null;
  improve: string | null;
  submittedAt: string | null;
}

/** The older public NPS form (callout.jp/feedback?user=<uid>). */
export interface NpsFeedbackListing {
  total: number;
  fromUsers: number;
  /** Net Promoter Score (-100..100) over scored responses from existing users. */
  nps: number | null;
  scored: number;
  promoters: number;
  passives: number;
  detractors: number;
  distribution: Record<string, number>;
  responses: NpsResponseRow[];
}

/** One item of the admin activity feed. `at` is when it last happened:
 * a send's latest batch, an email's send time, an answer's last change. */
export type ActivityItem =
  | ({ kind: 'send'; id: string; at: string | null } & EmailSendJob)
  | {
      kind: 'email';
      id: string;
      at: string | null;
      /** compose: from a send job no longer listed; automatic: a lifecycle
       * template; script: sent outside the dashboard (`emails.send_once`). */
      origin: 'compose' | 'automatic' | 'script';
      campaign: string | null;
      email: string | null;
      userId: string | null;
      subject: string | null;
      status: string | null;
      lang: string | null;
      error: string | null;
      jobId: string | null;
    }
  | ({ kind: 'survey'; at: string | null } & Omit<FeedbackRow, 'updatedAt'>)
  | ({ kind: 'nps'; at: string | null } & Omit<NpsResponseRow, 'submittedAt'>);

/** What the bot recorded a call consuming from the AI services. */
export interface CallUsage {
  /** "transcript" when rebuilt from the transcript of a call from before the bot recorded usage. */
  source?: 'transcript';
  llmModel: string | null;
  llmServiceTier: string | null;
  llmRequests: number;
  llmInputTokens: number;
  llmCachedInputTokens: number;
  llmOutputTokens: number;
  sttModel: string | null;
  sttSeconds: number;
  ttsModel: string | null;
  ttsCharacters: number;
}

/** One call's P&L in yen. A null amount isn't known yet (see PnlTotals). */
export interface PnlCall {
  callId: string;
  userId: string | null;
  email: string | null;
  toNumber: string | null;
  initiatedAt: string | null;
  status: string | null;
  duration: number;
  creditDeducted: boolean;
  reason: string | null;
  hasTranscript: boolean;
  /** Where the call was placed from: "web", "mcp" or "api"; null on older calls. */
  source: string | null;
  /** The payment the credit is valued from, e.g. "callout_10 (month)", or "free". */
  plan: string | null;
  revenue: number | null;
  stripeFee: number | null;
  twilio: number | null;
  /** Twilio's price per started minute. */
  twilioPerMinute: number | null;
  /** From Twilio Lookup: mobile, landline, fixedVoip, nonFixedVoip, tollFree, ... */
  lineType: string | null;
  carrier: string | null;
  llm: number | null;
  stt: number | null;
  tts: number | null;
  /** AI cost estimated: usage rebuilt from the transcript, or none and priced
   * at the window's average per minute. */
  aiEstimated: boolean;
  cost: number | null;
  profit: number | null;
  usage: CallUsage | null;
}

export interface PnlTotals {
  revenue: number;
  twilio: number;
  llm: number;
  stt: number;
  tts: number;
  stripeFee: number;
  cost: number;
  profit: number;
  calls: number;
  chargedCalls: number;
  connectedMinutes: number;
  margin: number | null;
  /** Calls Twilio hasn't priced yet; they count as 0 in the totals. */
  twilioPending: number;
  revenuePending: number;
  aiEstimated: number;
  aiUnknown: number;
  paymentsUnknown: number;
  /** Payments made in the window, net of tax. */
  cashCollected: number;
  /** Twilio's account total for the window minus the calls' prices. */
  twilioOther: number | null;
  /** Twilio's cost by the kind of line called, over the calls it has priced. */
  byLineType: Record<string, { calls: number; billedMinutes: number; twilio: number; perMinute: number | null }>;
}

export interface PnlReport {
  from: string;
  to: string;
  currency: 'JPY';
  totals: PnlTotals;
  assumptions: {
    usdJpy: number;
    stripeFeeRate: number;
    llmPerMillionTokens: Record<string, { input: number; cachedInput: number; output: number }>;
    deepgramPerMinute: Record<string, number>;
    cartesiaPerMillionChars: number;
    unpricedModels: string[];
  };
  stripeConfigured: boolean;
  twilioConfigured: boolean;
  calls: PnlCall[];
}

/** Landings, signups and call retention per site (GET /admin/growth, growth.py). */
export type GrowthSite = 'all' | 'jp' | 'us';

export interface GrowthCohort {
  /** Monday (UTC) of the signup week. */
  week: string;
  users: number;
  /** Users who started a call in week 0, 1, … after signing up; only weeks
   * every member has finished. */
  retained: number[];
}

export interface GrowthSiteMetrics {
  /** `ga4`: the day's counts come from Google Analytics (before the beacon). */
  landingsByDay: Record<string, { views: number; newVisitors: number; ga4?: boolean }>;
  signupsByDay: Record<string, number>;
  funnel: { newVisitors: number; signups: number; called: number; calledAgain: number };
  topPages: Record<string, number>;
  topSources: Record<string, number>;
  cohorts: GrowthCohort[];
}

export interface GrowthReport {
  days: number;
  since: string;
  /** First day with a landing visit counted, or null before any. */
  trackingSince: string | null;
  /** Last day filled from GA4, or null when no day of the window needed it. */
  ga4Until: string | null;
  /** Why GA4 couldn't be read (e.g. no GA4_PROPERTY_ID or no access). */
  ga4Error: string | null;
  sites: Record<GrowthSite, GrowthSiteMetrics>;
  /** Firebase Auth accounts created in the window that never got a profile
   *  (so they aren't counted as signups), oldest first; any site. */
  missingProfiles: { uid: string; email: string | null; createdAt: string }[];
  missingProfilesError: string | null;
}

export const adminApi = {
  growth: (days: number) => adminFetch<GrowthReport>(`/admin/growth?days=${days}`),
  pnl: (days: number) => adminFetch<PnlReport>(`/admin/pnl?days=${days}`),
  callPnl: (callId: string) =>
    adminFetch<{ call: PnlCall; assumptions: PnlReport['assumptions'] }>(`/admin/pnl/calls/${callId}`),
  summary: () => adminFetch<AnalyticsSummary>('/admin/analytics/summary'),
  users: () => adminFetch<{ users: AdminUserRow[]; count: number }>('/admin/users'),
  journeys: () => adminFetch<{ journeys: JourneyRecord[]; count: number }>('/admin/journeys'),
  userDetail: (uid: string) => adminFetch<UserDetail>(`/admin/users/${uid}`),
  callDetail: (callId: string) => adminFetch<CallDetail>(`/admin/calls/${callId}`),
  transcript: (callId: string) =>
    adminFetch<{ callId: string; transcript: TranscriptMessage[] }>(
      `/admin/calls/${callId}/transcript`
    ),
  npsFeedback: () => adminFetch<NpsFeedbackListing>('/admin/feedback/nps'),
  deletedCalls: () => adminFetch<DeletedCallsListing>('/admin/deleted-calls'),
  purgeDeletedCall: (callId: string) =>
    adminFetch<{ callId: string; deletionStatus: DeletionStatus }>(
      `/admin/deleted-calls/${encodeURIComponent(callId)}/purge`,
      { method: 'POST' }
    ),
};
